import axios from 'axios'
import { API, DOMAIN } from 'constants/api'
import {
  HistoryProps,
  HistoryStackContext,
  HistoryStackContextProps,
  HistoryStackProps,
} from 'contexts/HistoryStackContext'
import { useContext, useEffect, useLayoutEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

/**
 * ログを返却する
 * @param historyStack 操作履歴スタック
 * @param message ログメッセージ
 * @returns 生成されたログ
 */
const buildLog = (
  historyStack: HistoryStackProps,
  message: string
): LogProps => {
  return {
    history: historyStack,
    message,
    userAgent: window.navigator.userAgent,
  }
}

/**
 * 操作履歴スタックに新しい画面を追加する
 * @param historyStackContext 操作履歴スタック
 * @param page 画面タイトル
 */
export const addHistory = (
  historyStackContext: HistoryStackContextProps,
  page: string
): void => {
  // 現在の画面の操作履歴スタックを作成
  const [historyStack, setHistoryStack] = historyStackContext
  historyStack.push({
    page,
    inputs: [],
    action: '',
  })
  setHistoryStack(historyStack)
}

/**
 * ログを送信
 * @param historyStackContext 操作履歴スタック
 * @param message ログメッセージ
 */
export const sendLog = (
  historyStackContext: HistoryStackContextProps,
  message: string,
  jwt: string
): void => {
  const api = API.LOG
  const [historyStack] = historyStackContext
  const config = {
    ...api.config,
    headers: {
      Authorization: jwt,
    },
    url: `${DOMAIN}${api.config.url}`,
    data: buildLog(historyStack, message),
  }
  axios(config)
}

/**
 * 現在の画面の操作履歴を取得
 * @param historyStackContext 操作履歴スタック
 * @returns 現在の画面の操作履歴
 */
export const getCurrentHistory = (
  historyStackContext: HistoryStackContextProps
): HistoryProps => {
  const [historyStack] = historyStackContext
  return historyStack[historyStack.length - 1]
}

/**
 * 一つ前の画面の操作履歴を取得
 * @param historyStackContext 操作履歴スタック
 * @returns 一つ前の画面の操作履歴
 */
export const getPreviousHistory = (
  historyStackContext: HistoryStackContextProps
): HistoryProps => {
  const [historyStack] = historyStackContext
  return historyStack[historyStack.length - 2]
}

/**
 * 現在の画面を操作履歴スタックに追加する
 * @param page 画面タイトル
 */
export const useInitLog = (page: string): void => {
  // 現在の画面の操作履歴を作成
  const historyStackContext = useContext(HistoryStackContext)
  useLayoutEffect(() => {
    addHistory(historyStackContext, page)
  }, [page])
}

/**
 * 現在の操作履歴にアクションを追加する
 * @param historyStackContext 操作履歴スタック
 * @param actionName アクション名
 */
export const setHistoryAction = (
  historyStackContext: HistoryStackContextProps,
  actionName: string
): void => {
  const [historyStack, setHistoryStack] = historyStackContext
  const currentHistory = getCurrentHistory(historyStackContext)
  currentHistory.action = actionName
  setHistoryStack(historyStack)
}

/**
 * ブラウザの戻る、進むボタンを操作履歴へ反映させるためのHooksを起動する
 */
export const useListenBrowserAction = (): void => {
  // ブラウザの戻るボタン、進むボタンの検知と、操作履歴スタックへの反映
  const routerHistory = useHistory()
  const [historyStack, setHistoryStack] = useContext(HistoryStackContext)
  const [locationKeys, setLocationKeys] = useState<Array<string | undefined>>(
    []
  )
  useEffect(() => {
    return routerHistory.listen((location) => {
      if (routerHistory.action === 'PUSH') {
        setLocationKeys([location.key])
      } else if (routerHistory.action === 'POP') {
        const currentHistory = getPreviousHistory([
          historyStack,
          setHistoryStack,
        ])

        if (locationKeys[1] === location.key) {
          // ブラウザの進むボタン
          currentHistory.action = 'ブラウザフォワード'
          setLocationKeys(([, ...keys]) => keys)
        } else {
          // ブラウザの戻るボタン
          currentHistory.action = 'ブラウザバック'
          setLocationKeys((keys) => [location.key, ...keys])
        }
        setHistoryStack(historyStack)
      }
    })
  }, [locationKeys])
}

/**
 * 操作履歴の対象Inputの内容を更新する
 * @param inputName 項目名
 * @param inputValue 項目の値
 * @param watchValue 変更を監視する値
 */
export const updateLog = (
  inputName: string,
  inputValue: any,
  historyStackContext: HistoryStackContextProps
): void => {
  const [historyStack, setHistoryStack] = historyStackContext

  // 対象の画面の操作履歴
  const currentHistory = historyStack[historyStack.length - 1]

  // 対象のinput
  const currentInput =
    currentHistory.inputs &&
    currentHistory.inputs.find((input) => {
      return input.name === inputName
    })

  if (currentInput) {
    // 対象のinputがスタック済みならそれを変更
    currentInput.value = inputValue
  } else {
    // 対象のinputがなければ作成
    currentHistory.inputs.push({
      name: inputName,
      value: inputValue,
    })
  }

  console.log('historyStack', historyStack)
  setHistoryStack(historyStack)
}

/**
 * 操作履歴の対象Inputの内容を更新するHooks
 * @param inputName 項目名
 * @param inputValue 項目の値
 * @param watchValue 変更を監視する値
 */
export const useUpdateLog = (
  inputName: string,
  inputValue: any,
  watchValue: Array<any> | any
): void => {
  const historyStackContext = useContext(HistoryStackContext)
  useEffect(() => {
    updateLog(inputName, inputValue, historyStackContext)
  }, [inputName, inputValue, ...watchValue])
}

/**
 * 型定義：ログ
 */
export type LogProps = {
  history: HistoryStackProps
  message: string
  userAgent: string
}

/**
 * 型定義：ログ用テンプレート
 */
export type LogTemplateProps = {
  title: string
}
